import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';
import * as _ from 'underscore';

import * as graphql from 'generated/graphql';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import QuillText from 'packages/kakapo-ui-kit/components/editors/QuillText';
import Paragraph, { ParagraphSmall } from 'packages/kakapo-ui-kit/components/common/Paragraph';

import EvaluationNotes from './notes/EvaluationNotes';
import EvaluationSubmissionItemContents from './EvaluationSubmissionItemContents';

import { t } from 'i18next';
import CompetencyDisplay from './display/CompetencyDisplay';
import EvaluationSubmissionItemDevelopmentPlan from 'packages/kakapo-ui-application/pages/evaluation-submissions/EvaluationSubmissionItemDevelopmentPlan';
import EvaluationSubmissionRequired from 'packages/kakapo-ui-application/pages/evaluation-submissions/EvaluationSubmissionRequired';
import EvaluationSubmissionReviewGoals from './EvaluationSubmissionReviewGoals';
import { Callout } from '@treams/treams-ui';

const Container = styled(Flex)`
	&:not(:last-child) {
		margin-bottom: 64px;
	}
`;

const Description = styled.p`
	color: ${(props) => props.theme.colors.slateBlue};
	font-size: 14px;
	line-height: 24px;
`;

const SectionTitle = styled.h2`
	font-size: 24px;
	font-weight: bold;
`;

const QuestionNumber = styled.div`
	width: 32px;
	height: 32px;
	background: ${(props) => props.theme.colors.slateBlue};
	font-weight: bold;
	color: white;
	border-radius: 16px;
	line-height: 32px;
	text-align: center;
`;

const QuestionTitle = styled.div`
	font-size: 17px;
`;

const EvaluationSubmissionBody = ({
	type,
	item,
	section,
	submission,
	fillInBy,
	isRelevant,
	isRequired,
	onSave,
	isSaving,
}: {
	type: graphql.EvaluationSectionItemType;
	item: number;
	section: number;
	submission: graphql.EvaluationSubmission;
	fillInBy: {
		employee: boolean;
		manager: boolean;
	};
	isRelevant: boolean;
	isRequired: boolean;
	isSaving: boolean;
	onSave: () => void;
}) => {
	const user = submission.employee.user;

	if (type === graphql.EvaluationSectionItemType.DevelopmentPlan) {
		if (submission.employee.user.viewerCanViewDevelopmentPlan) {
			return <EvaluationSubmissionItemDevelopmentPlan userID={user.id} />;
		}

		return (
			<Callout
				title={t('evaluation.submission.developmentPlan.notVisible.title')}
				description={t('evaluation.submission.developmentPlan.notVisible.description')}
				color={'blue'}
			/>
		);
	}

	if (type === graphql.EvaluationSectionItemType.ReviewGoals) {
		return (
			<EvaluationSubmissionReviewGoals
				section={section}
				item={item}
				submission={submission}
				fillInBy={fillInBy}
				relevant={isRelevant}
				onSave={onSave}
				isSaving={isSaving}
			/>
		);
	}

	return (
		<EvaluationSubmissionItemContents
			section={section}
			item={item}
			submission={submission}
			fillInBy={fillInBy}
			required={isRequired}
			relevant={isRelevant}
			onSave={onSave}
			isSaving={isSaving}
		/>
	);
};

function EvaluationSubmissionTitle({ item }: { item: graphql.EvaluationSectionItem }) {
	const { t } = useTranslation();
	const { localize, language } = useLocalize();

	let title: graphql.LocalizedString | undefined = undefined;

	if (item.openQuestion) {
		title = item.openQuestion.title;
	}

	if (item.stars) {
		title = item.stars.title;
	}

	if (item.sentiment) {
		title = item.sentiment.title;
	}

	if (item.competency) {
		title = item.competency.competency.title;
	}

	if (item.reviewGoals) {
		title = [
			{
				languageID: language.id,
				text: t('evaluation.submission.reviewGoals.title'),
			},
		];
	}

	if (item.developmentPlan) {
		title = [
			{
				languageID: language.id,
				text: t('evaluation.submission.developmentPlan.title'),
			},
		];
	}

	return title ? (
		<Box>
			<QuestionTitle>{localize(title).text}</QuestionTitle>
		</Box>
	) : null;
}

export default memo(
	function EvaluationSubmissionItem(props: {
		section: number;
		item: number;
		submission: graphql.EvaluationSubmission;
		error: boolean;
		onSave: () => void;
		onNotesChange: () => void;
		isSaving: boolean;
	}) {
		const { t } = useTranslation();
		const { localize } = useLocalize();

		const item = props.submission.evaluationForm.sections[props.section].items[props.item];

		const ignored = [
			graphql.EvaluationSectionItemType.Title,
			graphql.EvaluationSectionItemType.Paragraph,
			graphql.EvaluationSectionItemType.FinalScore,
			graphql.EvaluationSectionItemType.Signature,
		];

		// To determine the question number, we count all preceding questions that should display a number.
		const index =
			props.submission.evaluationForm.sections
				.flatMap((section, sectionIndex) =>
					section.items
						// Save the original item index (i.e. before filtering) for counting later (which was also used for `props.item`).
						.map((item, itemIndex) => ({ item, itemIndex }))
						// Only include questions that are shown (cf. render condition below).
						.filter(({ item }) => {
							if (
								props.submission.employee.hasShared ||
								props.submission.manager.hasShared ||
								!props.submission.viewer.viewerCanUpdate
							) {
								return true;
							}

							return (
								item.fillInBy === graphql.EvaluationFillInBy.Both ||
								item.fillInBy.valueOf() === props.submission.viewer.party.valueOf()
							);
						})
						.map(({ item, itemIndex }) => ({
							sectionIndex,
							itemIndex,
							type: item.type,
						})),
				)
				// Exclude ignored 'question types' (i.e. visual elements such as titles and paragraphs).
				.filter(({ type }) => !ignored.includes(type))
				// Limit to all preceding question.
				.filter(
					({ sectionIndex, itemIndex }) =>
						sectionIndex < props.section ||
						(sectionIndex === props.section && itemIndex < props.item),
				).length + 1;

		if (
			(item.title || item.paragraph) &&
			!(
				item.fillInBy === graphql.EvaluationFillInBy.Both ||
				item.fillInBy.valueOf() === props.submission.viewer.party.valueOf() ||
				props.submission.employee.hasShared ||
				props.submission.manager.hasShared
			)
		) {
			return null;
		}

		if (item.title) {
			return (
				<Box mb={3}>
					<SectionTitle>{localize(item.title.text).text}</SectionTitle>
				</Box>
			);
		} else if (item.paragraph) {
			return (
				<Box mb={5}>
					<Description>
						<QuillText quill={localize(item.paragraph.text).text} />
					</Description>
				</Box>
			);
		} else if (ignored.includes(item.type)) {
			return null;
		} else {
			const fillInBy = {
				employee: [graphql.EvaluationFillInBy.Employee, graphql.EvaluationFillInBy.Both].includes(
					item.fillInBy,
				),
				manager: [graphql.EvaluationFillInBy.Manager, graphql.EvaluationFillInBy.Both].includes(
					item.fillInBy,
				),
			};

			const isRequired =
				fillInBy[props.submission.viewer.party.toLowerCase() as 'employee' | 'manager'];

			const isRelevant =
				props.submission.employee.hasShared || props.submission.manager.hasShared || isRequired;

			if (
				!(props.submission.employee.hasShared || props.submission.manager.hasShared) &&
				!isRelevant
			) {
				return null;
			}

			return (
				<Container flexWrap="wrap" alignItems="stretch">
					<Box flex={1} flexGrow={1}>
						<Flex alignItems="center" mb={3}>
							<Box flexShrink={0} flexGrow={0} mr={10}>
								<QuestionNumber>{index}</QuestionNumber>
							</Box>
							<EvaluationSubmissionTitle item={item} />
						</Flex>
						{item.competency && (
							<Box mb={3}>
								<Paragraph>
									<QuillText quill={localize(item.competency.competency.description ?? [])?.text} />
								</Paragraph>
							</Box>
						)}
						{item.reviewGoals && (
							<Box mb={3}>
								<ParagraphSmall color="slateBlue">
									{t('evaluation.submission.reviewGoals.description')}
								</ParagraphSmall>
							</Box>
						)}
						{props.error && (
							<EvaluationSubmissionRequired>
								{t('evaluation.submission.requiredForSigning')}
							</EvaluationSubmissionRequired>
						)}
						{item.type === graphql.EvaluationSectionItemType.Competency && (
							<CompetencyDisplay
								party={'both'}
								submission={props.submission}
								section={props.section}
								item={props.item}
							/>
						)}
						{!(
							item.type === graphql.EvaluationSectionItemType.Competency &&
							!props.submission.viewer.viewerCanUpdate
						) && (
							<EvaluationSubmissionBody
								section={props.section}
								item={props.item}
								type={item.type}
								submission={props.submission}
								fillInBy={fillInBy}
								isRelevant={isRelevant}
								isRequired={isRequired}
								onSave={props.onSave}
								isSaving={props.isSaving}
							/>
						)}
					</Box>
					{item.type !== graphql.EvaluationSectionItemType.DevelopmentPlan && (
						<Box
							ml={[0, 0, 0, 5]}
							mt={[3, 3, 3, 0]}
							flex={[1, 1, 1, 5 / 12]}
							style={{
								minWidth: 200,
								maxWidth: 300,
							}}
						>
							{(props.submission.viewer.hasShared || !props.submission.viewer.viewerCanUpdate) && (
								<EvaluationNotes
									onNotesChange={props.onNotesChange}
									submission={props.submission}
									itemID={item.id}
								/>
							)}
						</Box>
					)}
				</Container>
			);
		}
	},
	(a, b) => _.isEqual(_.omit(a, 'save'), _.omit(b, 'save')),
);
